import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Warning,
  CheckCircle,
  Error,
  Inventory,
  TrendingDown,
  People,
  ExpandMore
} from '@mui/icons-material';
import { stockVeterinaireAPI } from '../../services/stockService';

const StockVeterinairePage = () => {
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [tousLesStocks, setTousLesStocks] = useState([]);
  const [statistiquesGlobales, setStatistiquesGlobales] = useState(null);
  const [alertes, setAlertes] = useState([]);
  const [transferts, setTransferts] = useState([]);
  const [selectedTechnicien, setSelectedTechnicien] = useState('tous');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (tabValue === 0) {
        // Onglet Vue d'ensemble
        const [stocksRes, statsRes] = await Promise.all([
          stockVeterinaireAPI.getTousLesStocks(),
          stockVeterinaireAPI.getStatistiquesGlobales()
        ]);
        setTousLesStocks(stocksRes.data.data || []);
        setStatistiquesGlobales(statsRes.data.data || {});
      } else if (tabValue === 1) {
        // Onglet Alertes
        const alertesRes = await stockVeterinaireAPI.getToutesAlertes({ lu: false });
        setAlertes(alertesRes.data.data?.toutes || []);
      } else if (tabValue === 2) {
        // Onglet Transferts
        const transfertsRes = await stockVeterinaireAPI.getTousTransferts({});
        setTransferts(transfertsRes.data.data || []);
      }
    } catch (error) {
      showSnackbar('Erreur lors du chargement des données', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatutChip = (statut) => {
    const configs = {
      'ok': { label: 'OK', color: 'success', icon: <CheckCircle /> },
      'alerte': { label: 'Alerte', color: 'warning', icon: <Warning /> },
      'rupture': { label: 'Rupture', color: 'error', icon: <Error /> }
    };
    const config = configs[statut] || configs['ok'];
    return <Chip size="small" label={config.label} color={config.color} icon={config.icon} />;
  };

  const getTransfertStatutChip = (statut) => {
    const configs = {
      'en_attente': { label: 'En attente', color: 'warning' },
      'valide_source': { label: 'Validé source', color: 'info' },
      'valide_destination': { label: 'Validé destination', color: 'info' },
      'complete': { label: 'Complété', color: 'success' },
      'annule': { label: 'Annulé', color: 'error' }
    };
    const config = configs[statut] || { label: statut, color: 'default' };
    return <Chip size="small" label={config.label} color={config.color} />;
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Grouper les stocks par type
  const stockPrincipal = tousLesStocks.filter(s => s.type_stock === 'principal');
  const stocksTechniciens = tousLesStocks.filter(s => s.type_stock === 'technicien');

  // Obtenir la liste des techniciens uniques
  const techniciensUniques = [...new Set(stocksTechniciens.map(s => ({
    id: s.id_user,
    nom: `${s.nom_user} ${s.prenom_user}`
  })))].filter((v, i, a) => a.findIndex(t => t.id === v.id) === i);

  // Filtrer par technicien sélectionné
  const stocksFiltres = selectedTechnicien === 'tous' 
    ? stocksTechniciens 
    : stocksTechniciens.filter(s => s.id_user === selectedTechnicien);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Vue d'Ensemble - Tous les Stocks</Typography>
      </Box>

      {/* Statistiques Globales */}
      {statistiquesGlobales && tabValue === 0 && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Stocks Actifs
                    </Typography>
                    <Typography variant="h4">
                      {(statistiquesGlobales.stocks_techniciens?.length || 0) + 1}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      1 principal + {statistiquesGlobales.stocks_techniciens?.length || 0} techniciens
                    </Typography>
                  </Box>
                  <People color="primary" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Quantité Totale
                    </Typography>
                    <Typography variant="h4">
                      {statistiquesGlobales.totaux?.total_quantite || 0}
                    </Typography>
                  </Box>
                  <Inventory color="primary" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Alertes Totales
                    </Typography>
                    <Typography variant="h4" color="warning.main">
                      {statistiquesGlobales.totaux?.total_alertes || 0}
                    </Typography>
                  </Box>
                  <Warning color="warning" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Valeur Totale
                    </Typography>
                    <Typography variant="h4">
                      {parseFloat(statistiquesGlobales.totaux?.valeur_totale || 0).toFixed(2)} €
                    </Typography>
                  </Box>
                  <TrendingDown color="success" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Vue d'Ensemble" />
          <Tab label="Toutes les Alertes" />
          <Tab label="Tous les Transferts" />
        </Tabs>
      </Paper>

      {/* Onglet Vue d'ensemble */}
      {tabValue === 0 && (
        <Box>
          {/* Stock Principal */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box display="flex" alignItems="center" gap={2}>
                <Inventory color="primary" />
                <Typography variant="h6">Stock Principal du Cabinet</Typography>
                {statistiquesGlobales?.stock_principal && (
                  <Box display="flex" gap={1}>
                    <Chip 
                      size="small" 
                      label={`${statistiquesGlobales.stock_principal.nb_alertes || 0} alertes`}
                      color="warning"
                    />
                    <Chip 
                      size="small" 
                      label={`${statistiquesGlobales.stock_principal.nb_ruptures || 0} ruptures`}
                      color="error"
                    />
                  </Box>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell><strong>Médicament</strong></TableCell>
                      <TableCell align="center"><strong>Quantité</strong></TableCell>
                      <TableCell align="center"><strong>Seuil</strong></TableCell>
                      <TableCell align="center"><strong>Statut</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stockPrincipal.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography color="text.secondary">Aucun produit</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      stockPrincipal.map((produit, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{produit.nom_medicament}</TableCell>
                          <TableCell align="center">
                            <Typography 
                              variant="body1" 
                              fontWeight="bold"
                              color={produit.statut_stock === 'rupture' ? 'error' : 'inherit'}
                            >
                              {produit.quantite}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">{produit.seuil_alerte}</TableCell>
                          <TableCell align="center">
                            {getStatutChip(produit.statut_stock)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          {/* Stocks Techniciens */}
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box display="flex" alignItems="center" gap={2}>
                <People color="secondary" />
                <Typography variant="h6">Stocks des Techniciens</Typography>
                <Chip 
                  size="small" 
                  label={`${techniciensUniques.length} techniciens`}
                  color="primary"
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {/* Filtre par technicien */}
              <Box mb={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filtrer par technicien</InputLabel>
                  <Select
                    value={selectedTechnicien}
                    label="Filtrer par technicien"
                    onChange={(e) => setSelectedTechnicien(e.target.value)}
                  >
                    <MenuItem value="tous">Tous les techniciens</MenuItem>
                    {techniciensUniques.map((tech) => (
                      <MenuItem key={tech.id} value={tech.id}>
                        {tech.nom}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell><strong>Technicien</strong></TableCell>
                      <TableCell><strong>Médicament</strong></TableCell>
                      <TableCell align="center"><strong>Quantité</strong></TableCell>
                      <TableCell align="center"><strong>Seuil</strong></TableCell>
                      <TableCell align="center"><strong>Statut</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stocksFiltres.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography color="text.secondary">
                            Aucun produit pour ce technicien
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      stocksFiltres.map((produit, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            {produit.nom_user} {produit.prenom_user}
                          </TableCell>
                          <TableCell>{produit.nom_medicament}</TableCell>
                          <TableCell align="center">
                            <Typography 
                              variant="body1" 
                              fontWeight="bold"
                              color={produit.statut_stock === 'rupture' ? 'error' : 'inherit'}
                            >
                              {produit.quantite}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">{produit.seuil_alerte}</TableCell>
                          <TableCell align="center">
                            {getStatutChip(produit.statut_stock)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {/* Onglet Alertes */}
      {tabValue === 1 && (
        <Box>
          {alertes.length === 0 ? (
            <Paper>
              <Box p={3} textAlign="center">
                <CheckCircle color="success" sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Aucune alerte en cours dans tout le cabinet
                </Typography>
              </Box>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {/* Alertes Stock Principal */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" mb={2}>
                    Stock Principal
                  </Typography>
                  {alertes.filter(a => a.type_stock === 'principal').length === 0 ? (
                    <Alert severity="success">Aucune alerte</Alert>
                  ) : (
                    alertes
                      .filter(a => a.type_stock === 'principal')
                      .map((alerte) => (
                        <Alert 
                          key={alerte.id_alerte}
                          severity={alerte.niveau === 'danger' ? 'error' : 'warning'}
                          sx={{ mb: 1 }}
                        >
                          <Typography variant="body2">
                            <strong>{alerte.nom_medicament}</strong>
                          </Typography>
                          <Typography variant="caption">
                            Quantité: {alerte.quantite} / Seuil: {alerte.seuil_alerte}
                          </Typography>
                        </Alert>
                      ))
                  )}
                </Paper>
              </Grid>

              {/* Alertes Stocks Techniciens */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" mb={2}>
                    Stocks Techniciens
                  </Typography>
                  {alertes.filter(a => a.type_stock === 'technicien').length === 0 ? (
                    <Alert severity="success">Aucune alerte</Alert>
                  ) : (
                    alertes
                      .filter(a => a.type_stock === 'technicien')
                      .map((alerte) => (
                        <Alert 
                          key={alerte.id_alerte}
                          severity={alerte.niveau === 'danger' ? 'error' : 'warning'}
                          sx={{ mb: 1 }}
                        >
                          <Typography variant="body2">
                            <strong>{alerte.nom_user} {alerte.prenom_user}</strong> - {alerte.nom_medicament}
                          </Typography>
                          <Typography variant="caption">
                            Quantité: {alerte.quantite} / Seuil: {alerte.seuil_alerte}
                          </Typography>
                        </Alert>
                      ))
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      {/* Onglet Transferts */}
      {tabValue === 2 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Médicament</strong></TableCell>
                <TableCell><strong>Source</strong></TableCell>
                <TableCell><strong>Destination</strong></TableCell>
                <TableCell align="center"><strong>Quantité</strong></TableCell>
                <TableCell><strong>Demandeur</strong></TableCell>
                <TableCell><strong>Statut</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transferts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">
                      Aucun transfert enregistré
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                transferts.slice(0, 50).map((transfert) => (
                  <TableRow key={transfert.id_transfert} hover>
                    <TableCell>
                      {new Date(transfert.date_demande).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{transfert.nom_medicament}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {transfert.stock_source}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({transfert.type_stock_source})
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {transfert.stock_destination}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({transfert.type_stock_destination})
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={transfert.quantite} color="primary" size="small" />
                    </TableCell>
                    <TableCell>{transfert.demandeur}</TableCell>
                    <TableCell>
                      {getTransfertStatutChip(transfert.statut)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default StockVeterinairePage;
